/**
 * Create New Dealer Page (Master Admin)
 *
 * Captures the minimum information needed to bootstrap a dealer:
 *   - Name + optional description
 *   - Franchise Make (dropdown driven by franchise_makes_models.json)
 *     · "Used Cars Only" sentinel for independent used-car dealers — these
 *       skip the New-Vehicle pricing tab entirely.
 *
 * NOTE: The "Default Top Headline Text" field has been removed — that
 * setting now lives only in Unlock Branding where it belongs.
 */
import { useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import franchiseMakesModels from "../../data/franchise_makes_models.json";

// Sentinel value persisted in business.franchise_make for independent
// used-car dealers. The Pricing (New) tab is HIDDEN for dealers carrying
// this value.
export const USED_CARS_ONLY = "USED_CARS_ONLY";

const AdminDealerNewPage = ({ onNavigate }) => {
  const { apiFetch, user } = useAuth();
  const { success: showToast, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    franchise_make: "",
  });
  const [errors, setErrors] = useState({});

  const franchiseOptions = useMemo(() => Object.keys(franchiseMakesModels).sort(), []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Dealer name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    if (!formData.franchise_make) {
      newErrors.franchise_make =
        "Choose a franchise make (or 'Used Cars Only' for independent dealers)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const response = await apiFetch("/admin/dealers", {
        method: "POST",
        data: {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          franchise_make: formData.franchise_make,
        },
      });

      showToast("Dealer created successfully!");

      if (response.data?.id) {
        onNavigate("admin-dealer-detail", response.data.id);
      } else {
        onNavigate("admin-dealers");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to create dealer";
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // RBAC check
  if (user?.role !== "MASTER_ADMIN") {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-16 text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don&apos;t have permission to create dealers.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => onNavigate("admin-dealers")}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6 text-sm"
        data-testid="back-to-dealers-btn"
      >
        <span className="mr-2">←</span> Back to Dealers
      </button>

      <Card>
        <CardHeader>
          <CardTitle>Create New Dealer</CardTitle>
          <CardDescription>
            Add a new dealer to the Verified Demand platform. They will receive a unique public key and embed snippet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Dealer Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter dealer name"
                className={errors.name ? "border-red-500" : ""}
                data-testid="dealer-name-input"
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="franchise_make" className="text-sm font-medium">
                Franchise Make <span className="text-red-500">*</span>
              </Label>
              <select
                id="franchise_make"
                name="franchise_make"
                value={formData.franchise_make}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.franchise_make ? "border-red-500" : "border-gray-300"
                }`}
                data-testid="franchise-make-select"
              >
                <option value="">Select franchise make…</option>
                <option value={USED_CARS_ONLY}>Used Cars Only (No Franchise)</option>
                <optgroup label="Franchise Makes">
                  {franchiseOptions.map((make) => (
                    <option key={make} value={make}>{make}</option>
                  ))}
                </optgroup>
              </select>
              {errors.franchise_make && (
                <p className="text-sm text-red-500">{errors.franchise_make}</p>
              )}
              <p className="text-xs text-gray-500">
                Used Cars Only dealers see the Pricing (Used) tab only — Pricing (New)
                is hidden for them.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">
                Description
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Optional description for this dealer"
                rows={3}
                data-testid="dealer-description-input"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">📋 What happens next?</h4>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>A unique public key will be generated for this dealer</li>
                <li>An embed snippet will be available to copy</li>
                <li>You can configure Unlock branding and invite users</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onNavigate("admin-dealers")}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-dealer-submit-btn"
              >
                {loading ? "Creating..." : "Create Dealer"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDealerNewPage;
